import { t, Trans } from "@lingui/macro";
import { CheckIcon, XIcon } from "@phosphor-icons/react";
import type { comparePlans } from "@reactive-resume/utils";

type CellProps = { value: boolean | string };
const Cell = ({ value }: CellProps) => {
  if (value === true)
    return (
      <span className="grid size-5 place-items-center rounded-full bg-primary/10 text-primary">
        <CheckIcon size={12} weight="bold" />
      </span>
    );
  if (value === false) return <XIcon size={14} className="text-muted-foreground/50" />;
  return <span className="text-[13px] text-foreground">{value}</span>;
};

const localizeLabel = (value: string): string => {
  switch (value) {
    case "Resumes & exports": {
      return t`Resumes & exports`;
    }
    case "AI tools": {
      return t`AI tools`;
    }
    case "Job search & Apply Assist": {
      return t`Job search & Apply Assist`;
    }
    case "Analytics & support": {
      return t`Analytics & support`;
    }
    case "Resumes": {
      return t`Resumes`;
    }
    case "Templates": {
      return t`Templates`;
    }
    case "Premium templates (6 designs)": {
      return t`Premium templates (6 designs)`;
    }
    case "Public shareable resume link": {
      return t`Public shareable resume link`;
    }
    case "ATS score checker": {
      return t`ATS score checker`;
    }
    case "Resume roast": {
      return t`Resume roast`;
    }
    case "LinkedIn endorsement writer": {
      return t`LinkedIn endorsement writer`;
    }
    case "LinkedIn profile review": {
      return t`LinkedIn profile review`;
    }
    case "Resume tailoring (AI)": {
      return t`Resume tailoring (AI)`;
    }
    case "Job profiles": {
      return t`Job profiles`;
    }
    case "AI-scored job feed": {
      return t`AI-scored job feed`;
    }
    case "Tailored applications": {
      return t`Tailored applications`;
    }
    case "Gmail inbox signals": {
      return t`Gmail inbox signals`;
    }
    case "Advanced analytics dashboard": {
      return t`Advanced analytics dashboard`;
    }
    case "Email support": {
      return t`Email support`;
    }
    case "Unlimited": {
      return t`Unlimited`;
    }
    case "12 free": {
      return t`12 free`;
    }
    case "All 18": {
      return t`All 18`;
    }
    case "Full feed": {
      return t`Full feed`;
    }
    case "Community": {
      return t`Community`;
    }
    case "Standard": {
      return t`Standard`;
    }
    case "Priority": {
      return t`Priority`;
    }
    default: {
      const perDayAndMonth = /^(\d+) \/ day · (\d+) \/ mo$/.exec(value);
      if (perDayAndMonth) {
        const day = perDayAndMonth[1];
        const month = perDayAndMonth[2];
        return t`${day} / day · ${month} / mo`;
      }
      const perDay = /^(\d+) \/ day$/.exec(value);
      if (perDay) {
        const day = perDay[1];
        return t`${day} / day`;
      }
      const perMonth = /^(\d+) \/ mo$/.exec(value);
      if (perMonth) {
        const month = perMonth[1];
        return t`${month} / mo`;
      }
      const preview = /^(\d+)-match preview$/.exec(value);
      if (preview) {
        const count = preview[1];
        return t`${count}-match preview`;
      }
      return value;
    }
  }
};

const localizeGroups = (groups: ReturnType<typeof comparePlans>) =>
  groups.map((group) => ({
    name: localizeLabel(group.name),
    rows: group.rows.map((row) => ({
      feature: localizeLabel(row.feature),
      free: typeof row.free === "string" ? localizeLabel(row.free) : row.free,
      pro: typeof row.pro === "string" ? localizeLabel(row.pro) : row.pro,
      ultimate: typeof row.ultimate === "string" ? localizeLabel(row.ultimate) : row.ultimate,
    })),
  }));

export const PricingCompareTable = ({ groups }: { groups: ReturnType<typeof comparePlans> }) => {
  const localizedGroups = localizeGroups(groups);
  const plans = [t`Free`, t`Pro`, t`Ultimate`];
  const accessibleValue = (value: boolean | string) => {
    if (value === true) return t`Included`;
    if (value === false) return t`Not included`;
    return value;
  };

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface">
      <div data-testid="compare-desktop" className="hidden sm:block">
        <div className="grid grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))] border-b border-border bg-muted/40">
          <div className="px-6 py-4 text-xs font-medium text-muted-foreground">
            <Trans>Feature</Trans>
          </div>
          {[<Trans>Free</Trans>, <Trans>Pro</Trans>, <Trans>Ultimate</Trans>].map((n, index) => (
            <div
              key={index}
              className="px-6 py-4 text-center text-[13.5px] font-semibold text-foreground"
            >
              {n}
            </div>
          ))}
        </div>

        {localizedGroups.map((g) => (
          <div key={g.name}>
            <div className="grid grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))] border-b border-border bg-background/40">
              <div className="col-span-4 px-6 py-3 text-xs font-medium text-muted-foreground">
                {g.name}
              </div>
            </div>
            {g.rows.map((row, idx) => (
              <div
                key={`${row.feature}-${idx}`}
                className="grid grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))] border-b border-border last:border-0"
              >
                <div className="px-6 py-3.5 text-[13.5px] text-foreground/90">{row.feature}</div>
                {([row.free, row.pro, row.ultimate] as const).map((val, i) => (
                  <div
                    key={i}
                    role="group"
                    aria-label={`${plans[i]}: ${row.feature} — ${accessibleValue(val)}`}
                    className="flex items-center justify-center px-6 py-3.5"
                  >
                    <span aria-hidden="true">
                      <Cell value={val} />
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div data-testid="compare-mobile" className="sm:hidden">
        {localizedGroups.map((group) => (
          <div key={group.name}>
            <div className="border-b bg-muted/40 px-3 pb-2 pt-2.5">
              <div className="text-xs font-semibold">{group.name}</div>
              <div className="mt-1.5 grid grid-cols-3 text-center text-[12.5px] font-semibold">
                <span>
                  <Trans>Free</Trans>
                </span>
                <span className="text-primary">
                  <Trans>Pro</Trans>
                </span>
                <span>
                  <Trans>Ultimate</Trans>
                </span>
              </div>
            </div>
            {group.rows.map((row, index) => (
              <div key={`${row.feature}-${index}`} className="border-b px-3 py-2.5 last:border-0">
                <div className="text-[13px] font-medium">{row.feature}</div>
                <div className="mt-1.5 grid grid-cols-3 text-center text-[12.5px] leading-tight">
                  {([row.free, row.pro, row.ultimate] as const).map((value, i) => (
                    <div
                      key={i}
                      role="group"
                      aria-label={`${plans[i]}: ${row.feature} — ${accessibleValue(value)}`}
                      className={`flex min-w-0 items-center justify-center break-normal px-1 ${i === 1 ? "font-semibold" : ""}`}
                    >
                      <span aria-hidden="true">
                        <Cell value={value} />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
